import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from 'styled-components';
import { parse } from 'tldts';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import useIsClickedOutside from '@pkg/hooks/useIsClickedOutside';
import { useMediaQuery } from '@pkg/providers';

import { callMetaProxy } from '@app/helpers';
import { editItem, saveModifiedPageItems, setEditingItemId } from '@app/store/actions/yat-page-item-actions';
import { removeInvalidLink } from '@app/store/slices';
import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from '@app/store/slices/yat-page/yat-page';
import { type ContentItem, type LinkContent } from '@app/store/types/yat-page-actions';

import { DRAGGABLE_CLASS_NAME_TO_IGNORE } from '../../DraggableMasonry';

import YatTextInput from './TextInput';

import {
    CardEditWrapper,
    CardUrlWrapper,
    DescriptionSkeletonPlaceholder,
    Details,
    ErrorMessage,
    GlowBackground,
    GridItemUrlWrapper,
    Image,
    StyledDescriptionTypography,
    StyledTitleTypography,
    StyledTypography,
    StyledUrlTypography,
    StyledYatImageToggle,
    StyledYatRemoveButton,
    TitleSkeletonPlaceholder,
    UrlIcon,
    UrlWrapper,
} from './styles';

interface Props {
    item: ContentItem<LinkContent>;
    editMode?: boolean;
}

interface PageMetaItem {
    content: string;
    property: string;
    name?: string;
}

interface PageMetas {
    meta: PageMetaItem[];
}

export function YatGridItemLink({ item, editMode }: Props) {
    const theme = useContext(ThemeContext);
    const { usingMouse } = useMediaQuery();
    const { t } = useTranslation('yat_page');
    const dispatch = useAppDispatch();
    const draggingId = useAppSelector((state) => state.yatPage.draggingId);
    const { description, image, url, title } = item.content ?? ({} as LinkContent);
    const collapsed = item.collapsed;
    const dragging = draggingId === item.id;

    const cardUrl = useRef(url);
    const [visibleCardUrl, setVisibleCardUrl] = useState<string>(parse(url).domain || '');
    const [urlInputFocused, setURLInputFocused] = useState(!url);
    const [urlInvalid, setURLInvalid] = useState(false);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);
    const [showUrlMaskToFadeOverflow, setShowUrlMaskToFadeOverflow] = useState(false);
    const wrapperDivRef = useRef<HTMLDivElement>(null);
    const wrapperAnchorRef = useRef<HTMLAnchorElement>(null);
    const detailsRef = useRef<HTMLDivElement>(null);
    const urlWrapperRef = useRef<HTMLDivElement>(null);
    const loadingUrlRef = useRef<string>();
    const loadUrlTimeout = useRef<NodeJS.Timeout>();

    useIsClickedOutside(
        wrapperDivRef.current,
        useCallback(
            (isClickedOutside) => {
                if (editMode && isClickedOutside) {
                    setURLInputFocused(false);
                    if (usingMouse) dispatch(setEditingItemId());
                }
            },
            [dispatch, editMode, usingMouse],
        ),
    );

    const updateItem = useCallback(
        (updatedItem: {
            collapsed?: boolean;
            content?: {
                url?: string;
                description?: string;
                title?: string;
                image?: string;
            };
            isURLInvalid?: boolean;
        }) => {
            if (item.content) {
                const collapsedProp = updatedItem.collapsed !== undefined ? updatedItem.collapsed : item.collapsed;
                const invalidUrlProp =
                    updatedItem.isURLInvalid !== undefined ? updatedItem.isURLInvalid : item.isURLInvalid;
                dispatch(
                    editItem({
                        ...item,
                        collapsed: collapsedProp,
                        isURLInvalid: invalidUrlProp,
                        content: {
                            ...item.content,
                            ...updatedItem.content,
                        },
                    }),
                );
            }
        },
        [dispatch, item],
    );

    const handleChangeMeta = useCallback(
        (url: string, metaTags: PageMetas) => {
            const title =
                metaTags.meta
                    .find((tag) => {
                        return tag.property === 'og:title' || tag.property === 'og:site_name';
                    })
                    ?.content.slice(0, TITLE_MAX_LENGTH) || '';

            // Try to get the og:description first
            let description = metaTags.meta
                .find((tag) => tag.property === 'og:description')
                ?.content.slice(0, DESCRIPTION_MAX_LENGTH);

            if (!description) {
                description =
                    metaTags.meta.find((tag) => tag.name === 'description')?.content.slice(0, DESCRIPTION_MAX_LENGTH) ||
                    '';
            }

            const image =
                metaTags.meta.find((tag) => {
                    return tag.property === 'og:image';
                })?.content || '';
            updateItem({ content: { description, title, url, image }, isURLInvalid: false });

            if (item.hasPersisted) {
                dispatch(saveModifiedPageItems());
            }
        },
        [dispatch, item.hasPersisted, updateItem],
    );

    const getWidthMinusPadding = (element: HTMLDivElement) => {
        const UrlWrapperStyle = window.getComputedStyle(element);

        // Convert PX string to number
        const paddingRight = parseInt(UrlWrapperStyle.paddingRight, 10);
        const paddingLeft = parseInt(UrlWrapperStyle.paddingLeft, 10);

        return element.clientWidth - paddingRight - paddingLeft;
    };

    const handleChangeUrl = (url: string) => {
        updateItem({ content: { url }, isURLInvalid: true });
        cardUrl.current = url;
        setVisibleUrl(url);
        loadingUrlRef.current = addHttpProtocol(url);
        loadUrlTimeout.current && clearTimeout(loadUrlTimeout.current);
        loadUrlTimeout.current = setTimeout(() => {
            handleFetchMetadata(url);
        }, 300);
    };

    const loadUrl = useCallback(
        (url: string) => {
            setIsLoadingUrl(true);
            callMetaProxy({ url }).then((result) => {
                if (loadingUrlRef.current && loadingUrlRef.current == url) {
                    if (result.ok && result.value) {
                        if (typeof result.value === 'object') {
                            handleChangeMeta(url, result.value);
                        }
                        setURLInvalid(false);
                        dispatch(removeInvalidLink());
                    } else {
                        setURLInvalid(true);
                        updateItem({ isURLInvalid: true, content: { image: '' } });
                    }
                    setIsLoadingUrl(false);
                }
            });
        },
        [handleChangeMeta, updateItem, dispatch],
    );

    const setVisibleUrl = useCallback((url) => {
        const parsedUrl = parse(url);
        const urlDomain = parsedUrl.domain;
        setVisibleCardUrl(urlDomain || url);
    }, []);

    const handleImageToogle = () => {
        updateItem({ collapsed: !collapsed });
        dispatch(saveModifiedPageItems());
    };

    const handleURLOnBlur = () => {
        if (editMode && urlInputFocused && cardUrl.current) {
            setURLInputFocused(false);
        }
    };

    const handleInfoOnBlur = () => {
        dispatch(saveModifiedPageItems());
    };

    const addHttpProtocol = (newUrl: string) => {
        let url = newUrl.slice(0);
        if (newUrl.indexOf('http://') !== 0 && newUrl.indexOf('https://') !== 0) {
            url = `https://${newUrl}`;
        }
        return url;
    };

    const handleFetchMetadata = useCallback(
        (newUrl: string) => {
            updateItem({ content: { image: '' } });
            loadUrl(addHttpProtocol(newUrl));
        },
        [loadUrl, updateItem],
    );

    const handleImageLoad = () => {
        !usingMouse &&
            setTimeout(() => {
                wrapperDivRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 400);
    };

    const updateLinkOverflowMask = useCallback(() => {
        if (!detailsRef.current || !urlWrapperRef.current) return;

        const detailsWidth = getWidthMinusPadding(detailsRef.current);
        const UrlWrapperWidth = urlWrapperRef.current.offsetWidth;

        // If link wrapper is same width as details wrapper, then show link overflow mask
        setShowUrlMaskToFadeOverflow(detailsWidth === UrlWrapperWidth);
    }, []);

    useEffect(() => {
        if (editMode) return;

        // Run on initial load
        updateLinkOverflowMask();

        window.addEventListener('resize', updateLinkOverflowMask);
        return () => window.removeEventListener('resize', updateLinkOverflowMask);
    }, [editMode, updateLinkOverflowMask]);

    useEffect(() => {
        const urlWithProtocol = addHttpProtocol(url);

        if (urlWithProtocol != addHttpProtocol(cardUrl.current) && !usingMouse) {
            setVisibleUrl(url);
            cardUrl.current = urlWithProtocol;
            loadingUrlRef.current = urlWithProtocol;
            loadUrlTimeout.current && clearTimeout(loadUrlTimeout.current);
            loadUrlTimeout.current = setTimeout(() => handleFetchMetadata(urlWithProtocol), 600);
        }
    }, [url, setVisibleUrl, handleFetchMetadata, cardUrl, usingMouse, urlInputFocused]);

    const handleClickUrlInput = () => {
        if (usingMouse && editMode) {
            setURLInputFocused(true);
        }
    };

    const cardInner = (
        <>
            <Image>
                <AnimatePresence>
                    {image && !collapsed && (
                        <motion.img
                            key={`card-image-${image}`}
                            src={image}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'unset' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{
                                duration: theme.transitions.speeds.normal,
                                ease: theme.transitions.eases.on,
                            }}
                            onLoad={handleImageLoad}
                            onError={(e) => {
                                // Avoid infinite loop
                                e.currentTarget.onerror = null;
                                // Set the src to a single transparent pixel
                                e.currentTarget.src =
                                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                                e.currentTarget.style.height = '0';
                                e.currentTarget.style.width = '0';
                            }}
                        />
                    )}
                </AnimatePresence>
            </Image>
            <Details ref={detailsRef}>
                {(!url || item.isURLInvalid) && (
                    <>
                        <TitleSkeletonPlaceholder $loading={isLoadingUrl} />
                        <DescriptionSkeletonPlaceholder $loading={isLoadingUrl} />
                    </>
                )}

                {url && !item.isURLInvalid && (
                    <>
                        <StyledTitleTypography type="headline" size={24}>
                            {usingMouse && editMode ? (
                                <YatTextInput
                                    value={title}
                                    placeholderText={t('add_card_title_placeholder')}
                                    characterLimit={TITLE_MAX_LENGTH}
                                    multipleLines
                                    showBackgroundWhenHasFocus
                                    onBlur={handleInfoOnBlur}
                                    onChange={(value) => updateItem({ content: { title: value } })}
                                />
                            ) : (
                                title
                            )}
                        </StyledTitleTypography>

                        <StyledDescriptionTypography type="paragraph" size={16}>
                            {usingMouse && editMode ? (
                                <YatTextInput
                                    value={description}
                                    placeholderText={t('add_card_description_placeholder')}
                                    characterLimit={DESCRIPTION_MAX_LENGTH}
                                    multipleLines
                                    showBackgroundWhenHasFocus
                                    onBlur={handleInfoOnBlur}
                                    onChange={(value) => updateItem({ content: { description: value } })}
                                />
                            ) : (
                                description
                            )}
                        </StyledDescriptionTypography>
                    </>
                )}

                <UrlWrapper
                    ref={urlWrapperRef}
                    $editMode={editMode}
                    $hasFocus={urlInputFocused}
                    $hasValidUrl={!!url && !item.isURLInvalid}
                    $isURLInvalid={urlInvalid}
                    className={DRAGGABLE_CLASS_NAME_TO_IGNORE}
                    onClick={handleClickUrlInput}
                >
                    <StyledUrlTypography className={DRAGGABLE_CLASS_NAME_TO_IGNORE} type="paragraph" size={14}>
                        {urlInputFocused ? (
                            <YatTextInput
                                value={cardUrl.current}
                                placeholderText={usingMouse ? t('add_card_url_placeholder') : undefined}
                                hidePlaceholderInTextArea
                                selectAllTextOnEdit
                                showMaskToFadeOverflow={editMode || showUrlMaskToFadeOverflow}
                                autoFocus
                                onChange={handleChangeUrl}
                                onBlur={handleURLOnBlur}
                            />
                        ) : (
                            visibleCardUrl
                        )}
                    </StyledUrlTypography>

                    <AnimatePresence initial={false}>
                        {url && !item.isURLInvalid && (
                            <UrlIcon
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: theme.transitions.speeds.normal }}
                            />
                        )}
                    </AnimatePresence>
                </UrlWrapper>
                {urlInvalid && (
                    <StyledTypography type="label" weight="400" size={12}>
                        <ErrorMessage>{t('invalid_url_address')}</ErrorMessage>
                    </StyledTypography>
                )}
            </Details>
        </>
    );

    const animateBackgroundShine = useCallback(
        (animate = false) => {
            if (!wrapperDivRef.current && !wrapperAnchorRef.current) return;

            gsap.to(wrapperDivRef.current || wrapperAnchorRef.current, {
                background: `radial-gradient(
                    50% 159.44% at ${animate ? 85 : 0}% ${animate ? -33 : 120}%,
                    ${theme.shades.white[10]} 0%,
                    transparent 100%
                ),
                linear-gradient(179.87deg, #1D1B2B 0%, #090719 100%)`,
                duration: theme.transitions.speeds.normal,
                ease: 'power2.out',
            });
        },
        [theme],
    );

    // Set background on load
    useEffect(() => animateBackgroundShine(), [animateBackgroundShine]);

    const sharedWrapperProps = {
        onMouseEnter: () => animateBackgroundShine(true),
        onMouseLeave: () => animateBackgroundShine(),
        $hasImage: !!image,
    };

    const cardWrapper = editMode ? (
        <CardEditWrapper ref={wrapperDivRef} {...sharedWrapperProps}>
            {cardInner}
        </CardEditWrapper>
    ) : (
        <CardUrlWrapper ref={wrapperAnchorRef} {...sharedWrapperProps} href={url} target="_blank">
            {cardInner}
        </CardUrlWrapper>
    );

    const handleMobileEditToggle = () => {
        if (!usingMouse && editMode) {
            dispatch(setEditingItemId(item.id));
            setTimeout(() => {
                wrapperDivRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 400);
        }
    };

    return (
        <GridItemUrlWrapper
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: theme.transitions.speeds.normal }}
            onClick={handleMobileEditToggle}
        >
            {editMode && (
                <>
                    <StyledYatImageToggle
                        canShow={!!image && image.toLocaleLowerCase().startsWith('http') && !dragging}
                        showingImage={!collapsed}
                        onClick={handleImageToogle}
                    />

                    <StyledYatRemoveButton canShow={!dragging} item={item} />
                </>
            )}

            {cardWrapper}

            <AnimatePresence>
                {!dragging && (
                    <GlowBackground
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: theme.transitions.speeds.normal }}
                    />
                )}
            </AnimatePresence>
        </GridItemUrlWrapper>
    );
}
